import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Icon } from "../icons";
import { fmtDateTime } from "../lib/format";
import { resolveTemplate } from "../lib/messageTemplates";

// Critical layout inline so the popup always centers even if the hashed CSS
// bundle is momentarily out of sync with the JS after a deploy.
const OVERLAY_STYLE = {
  position: "fixed",
  inset: 0,
  zIndex: 240,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "22px 16px",
  background: "rgba(15,23,42,.5)",
  overflowY: "auto",
};
const CARD_STYLE = {
  background: "#fff",
  width: "100%",
  maxWidth: 560,
  borderRadius: 18,
  boxShadow: "0 16px 40px rgba(15,23,42,.24)",
  maxHeight: "92vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

/**
 * Compose / preview popup for the "Send text" and "Send email" actions. The
 * coordinator sees the resolved template, can EDIT it, then sends. A per-job
 * history shows who sent what and when.
 */
export const ComposeMessageModal = ({ job, channel, template, coordinator, onSend, onClose }) => {
  const isEmail = channel === "email";
  const to = isEmail ? (job.email || "") : (job.phone || "");
  const customTemplates = useSelector((s) => s.mcOpsDashboard?.meta?.templates || []);

  const draft = useMemo(
    () =>
      resolveTemplate(
        channel,
        template,
        { client: job.clientName, coordinator, moveDate: job.moveDate },
        customTemplates,
      ),
    [channel, template, job.clientName, job.moveDate, coordinator, customTemplates],
  );

  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const history = (job.sentMessages || [])
    .filter((m) => m.channel === channel)
    .slice()
    .reverse();

  const canSend = !!to && body.trim() !== "" && (!isEmail || subject.trim() !== "");

  const doSend = () => {
    if (!canSend || sending) return;
    setSending(true);
    onSend({ channel, to, subject: isEmail ? subject.trim() : "", body: body.trim(), template });
    setSent(true);
    setTimeout(onClose, 900);
  };

  return (
    <div
      className="cmp-overlay"
      style={OVERLAY_STYLE}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="cmp-card" role="dialog" aria-modal="true" style={CARD_STYLE}>
        <div className="cmp-head">
          <div className="cmp-title">
            <span className={`cmp-badge cmp-badge-${channel}`}>
              <Icon name={isEmail ? "mail" : "msg"} />
            </span>
            <div>
              <div className="cmp-h1">{isEmail ? "Send email" : "Send text"}</div>
              <div className="cmp-h2">{template} · {job.clientName}</div>
            </div>
          </div>
          <button className="cmp-x" onClick={onClose} type="button" aria-label="Close">
            <Icon name="x" />
          </button>
        </div>

        <div className="cmp-body">
          <label className="cmp-label">{isEmail ? "To (email)" : "To (phone)"}</label>
          <div className={`cmp-to${to ? "" : " cmp-to-missing"}`}>
            {to || (isEmail ? "No client email on file" : "No client phone on file")}
          </div>

          {isEmail ? (
            <>
              <label className="cmp-label">Subject</label>
              <input
                className="cmp-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
              />
            </>
          ) : null}

          <label className="cmp-label">Message <span className="cmp-hint">(edit before sending)</span></label>
          <textarea
            className="cmp-textarea"
            rows={isEmail ? 9 : 5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          {!isEmail ? (
            <div className="cmp-count">{body.length} characters</div>
          ) : null}

          {history.length ? (
            <div className="cmp-history">
              <div className="cmp-hist-title">Previously sent ({history.length})</div>
              {history.slice(0, 5).map((m, i) => (
                <div className="cmp-hist-item" key={m.id || i}>
                  <div className="cmp-hist-meta">
                    <Icon name="check" /> {m.sentBy || "MC"} · {fmtDateTime(m.sentAt)}
                  </div>
                  {m.subject ? <div className="cmp-hist-subj">{m.subject}</div> : null}
                  <div className="cmp-hist-body">{m.body}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="cmp-foot">
          <button className="btn" type="button" onClick={onClose} disabled={sending}>Cancel</button>
          <button
            className={`btn ${sent ? "success" : "primary"}`}
            type="button"
            onClick={doSend}
            disabled={!canSend || sending}
          >
            {sent ? (<><Icon name="check" /> Sent</>) : sending ? "Sending…" : (<><Icon name={isEmail ? "mail" : "msg"} /> Send {isEmail ? "email" : "text"}</>)}
          </button>
        </div>
      </div>
    </div>
  );
};
